import AddIcon from "@mui/icons-material/Add";
import { Stack, styled, Tabs } from "@mui/material";
import Box from "@mui/material/Box";
import { ChangeEvent, FC, SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Breadcrumbs from "../components/breadcrumbs";
import Goods from "../components/goods";
import ImportTooltip from "../components/goods/components/import-tooltip";
import { DocsIcon, DocumentIcon, DownloadIcon, Upload01Icon } from "../components/icons";
import SellerTokens from "../components/seller-tokens";
import StyledButton from "../components/ui/StyledButton";
import StyledTab from "../components/ui/StyledTab";
import { useStore } from "../hooks";

type TabsCodes = "sellerTokens" | "goods";

const TABS: Array<TabsCodes> = ["sellerTokens", "goods"];

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Settings: FC = () => {
  const { id } = useParams<{ id: TabsCodes }>();
  const { push } = useHistory();
  const [tabId, setTabId] = useState(id ? TABS.indexOf(id) : 0);
  const [showTokensAddForm, setShowTokensAddForm] = useState(false);
  const [showGoodsAddForm, setShowGoodsAddForm] = useState(false);

  const {
    goodsStore: {
      goods,
      exportFile,
      importFile,
    },
  } = useStore();

  const onChange = (event: SyntheticEvent, id: number) => {
    const value = TABS[id];

    push(`/settings/${value}`);
    setTabId(id);
  };

  const onImport = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append("file", file, file.name);

      importFile(formData);
    }
  }, [importFile]);

  const renderTab = useMemo(() => {
    const tabCode = TABS[tabId];

    if (tabCode === "sellerTokens") {
      return <SellerTokens showAddForm={showTokensAddForm} setShowAddForm={setShowTokensAddForm} />;
    }

    if (tabCode === "goods") {
      return <Goods showAddForm={showGoodsAddForm} setShowAddForm={setShowGoodsAddForm} />;
    }
  }, [tabId, showTokensAddForm, showGoodsAddForm, setShowTokensAddForm, setShowGoodsAddForm]);

  const renderButtons = useMemo(() => {
    const tabCode = TABS[tabId];

    if (tabCode === "sellerTokens") {
      return (
        <StyledButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowTokensAddForm(true)}
        >
          Добавить
        </StyledButton>
      );
    }

    if (tabCode === "goods") {
      return (
        <Stack direction="row" gap="12px">
          <ImportTooltip hasGoods={Boolean(goods?.length)}>
            <StyledButton
              component="label"
              variant="outlined"
              startIcon={<Upload01Icon />}
            >
              Импорт
              <VisuallyHiddenInput
                type="file"
                onChange={onImport}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </StyledButton>
          </ImportTooltip>

          <StyledButton
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportFile}
          >
            Экспорт
          </StyledButton>

          <StyledButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowGoodsAddForm(true)}
          >
            Добавить
          </StyledButton>
        </Stack>
      );
    }
  }, [tabId, exportFile, onImport, goods]);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "#F7F9FB",
          padding: "20px 24px",
          borderBottom: "1px solid #E0E4EE",
          width: "calc(100% + 48px)",
          marginLeft: "-24px",
          marginTop: "-24px"
        }}
      >
        <Breadcrumbs pageName="Настройки" />
        {renderButtons}
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: "divider", my: "18px" }}>
        <Tabs value={tabId} onChange={onChange}>
          <StyledTab icon={<DocumentIcon />} iconPosition="start" label="Токены" id="sellerTokens" />
          <StyledTab icon={<DocsIcon />} iconPosition="start" label="Товары" id="goods" />
        </Tabs>
      </Box>

      {renderTab}
    </Box>
  );
};

export default Settings;
